// src/components/JobsList.jsx — Active & recent skill jobs sidebar section
// Subscribes to skill_jobs via Supabase Realtime for live status updates.

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { colors, fonts } from '../theme';

// Thème local dark-aware : chaque valeur est un token DS (`var(--…)`) qui se
// résout selon `<html data-theme>`. On garde l'objet `C` (mêmes clés) pour ne
// toucher qu'aux valeurs, pas à la structure du composant.
const C = {
  navy: colors.textPrimary,   // ink → texte principal
  gold: colors.accent,        // brand bordeaux
  goldL: colors.accentDeep,   // brand-deep
  green: colors.statusOk,     // status-ok
  red: colors.statusMiss,     // status-miss
  txM: colors.textSecondary,
  txL: colors.textMuted,
  hover: colors.bgHover,      // survol d'une ligne (dark-aware)
  sans: fonts.sans,
  mono: fonts.mono,
};

const STATUS = {
  pending: { label: 'En attente', color: C.txL, icon: '⏳' },
  running: { label: 'En cours', color: C.gold, icon: '⚡' },
  completed: { label: 'Terminé', color: C.green, icon: '✓' },
  error: { label: 'Erreur', color: C.red, icon: '✗' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'à l\'instant';
  if (diff < 3600000) return `il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `il y a ${Math.floor(diff / 3600000)}h`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function JobsList({ lawyerId, onJobClick }) {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (!lawyerId) return;

    // Load recent jobs (last 24h or still active)
    async function loadJobs() {
      const since = new Date(Date.now() - 24 * 3600000).toISOString();
      const { data } = await supabase
        .from('skill_jobs')
        .select('id, skill_slug, status, current_step, total_steps, current_step_label, created_at, completed_at, error_message')
        .eq('lawyer_id', lawyerId)
        .or(`status.in.(pending,running),created_at.gte.${since}`)
        .order('created_at', { ascending: false })
        .limit(10);
      if (data) setJobs(data);
    }

    loadJobs();

    // Subscribe to changes
    const channel = supabase
      .channel('jobs_list')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'skill_jobs',
          filter: `lawyer_id=eq.${lawyerId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setJobs(prev => [payload.new, ...prev].slice(0, 10));
          } else if (payload.eventType === 'UPDATE') {
            setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j));
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [lawyerId]);

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'running');
  const recentJobs = jobs.filter(j => j.status === 'completed' || j.status === 'error');

  if (jobs.length === 0) return null;

  return (
    <div style={{ padding: '12px 0' }}>
      {activeJobs.length > 0 && (
        <>
          <div style={{
            fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: C.gold, padding: '0 16px',
            marginBottom: 8, fontFamily: C.sans,
          }}>
            Études en cours
          </div>
          {activeJobs.map(job => (
            <JobItem key={job.id} job={job} onClick={() => onJobClick?.(job)} />
          ))}
        </>
      )}
      {recentJobs.length > 0 && (
        <>
          <div style={{
            fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: C.txL, padding: '0 16px',
            marginTop: activeJobs.length > 0 ? 12 : 0, marginBottom: 8, fontFamily: C.sans,
          }}>
            Études récentes
          </div>
          {recentJobs.map(job => (
            <JobItem key={job.id} job={job} onClick={() => onJobClick?.(job)} />
          ))}
        </>
      )}
    </div>
  );
}

function JobItem({ job, onClick }) {
  const st = STATUS[job.status] || STATUS.pending;
  const progress = job.total_steps > 0
    ? `${job.current_step || 0}/${job.total_steps}`
    : null;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 16px', cursor: 'pointer',
        transition: 'background 0.15s',
        borderRadius: 0,
      }}
      onMouseEnter={e => e.currentTarget.style.background = C.hover}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{
        fontSize: '0.85rem',
        width: 20, textAlign: 'center',
        ...(job.status === 'running' ? { animation: 'pulse 1.2s ease-in-out infinite' } : {}),
      }}>
        {st.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.78rem', fontWeight: 500, color: C.navy,
          fontFamily: C.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {job.skill_slug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Analyse'}
        </div>
        <div style={{ fontSize: '0.68rem', color: C.txM, fontFamily: C.sans }}>
          {job.status === 'running' && job.current_step_label
            ? job.current_step_label
            : st.label}
          {progress && job.status === 'running' ? ` (${progress})` : ''}
          {' · '}
          {timeAgo(job.created_at)}
        </div>
      </div>
      {job.status === 'running' && (
        <div style={{
          width: 6, height: 6, borderRadius: '50%', background: C.gold,
          animation: 'pulse 1.2s ease-in-out infinite', flexShrink: 0,
        }} />
      )}
    </div>
  );
}
